import Panel from '@/components/panel/Panel.jsx';
import { FiDatabase } from '@/components/react-icons/main';
import Select from '@/components/Select.jsx';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import argumentDataSources from '@/schema/argumentDataSources.js';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import ArgumentLabel from '../ArgumentLabel.jsx';
import ArgumentWrapper from '../ArgumentWrapper.jsx';
import SliderClose from './SliderClose.jsx';
import SliderHeader from './SliderHeader.jsx';
import SliderTitle from './SliderTitle.jsx';

const conditionOptions = [
  { label: 'On Every Run', value: 'always' },
  { label: 'In Development', value: 'development' }
];

const ttlOptions = [
  { value: 1000 * 60 * 10, label: 'For 10 Minutes' },
  { value: 1000 * 60 * 30, label: 'For 30 Minutes' },
  { value: 1000 * 60 * 60, label: 'For 1 Hour' },
  { value: 1000 * 60 * 60 * 3, label: 'For 3 Hours' },
  { value: 1000 * 60 * 60 * 6, label: 'For 6 Hours' },
  { value: 1000 * 60 * 60 * 12, label: 'For 12 Hours' },
  { value: 1000 * 60 * 60 * 24, label: 'For 1 Day' },
  { value: 1000 * 60 * 60 * 24 * 2, label: 'For 2 Days' },
  { value: 1000 * 60 * 60 * 24 * 3, label: 'For 3 Days' },
  { value: 1000 * 60 * 60 * 24 * 7, label: 'For 1 Week' },
  { value: 1000 * 60 * 60 * 24 * 14, label: 'For 2 Weeks' },
  { value: 1000 * 60 * 60 * 24 * 30, label: 'For 1 Month' }
];

const CacheSlider = ({ onClose, dataSource, dataSourcePath, dataSavePath, disabled = false }) => {
  const options = useSelector(argumentSelectors[dataSource].activeOptions);
  const actions = dataSource === argumentDataSources.workflowStep ? stepActions : appActions;
  const value = options?.cache;

  const handleDelete = () => {
    actions.set(`${dataSavePath ? `${dataSavePath}.options` : dataSourcePath}`, (prev) => ({ ...prev, cache: null }));
    onClose();
  };

  const handleCacheChange = (key, value) => {
    actions.set(`${dataSavePath ? `${dataSavePath}.options` : dataSourcePath}.cache`, (prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    setTimeout(() => {
      document.querySelector('.cache-slider .comp-input input')?.focus();
    }, 200);
  }, []);

  return (
    <>
      <SliderHeader showDelete showRun onClose={onClose} onDelete={handleDelete} disabled={disabled} />
      <SliderTitle //
        icon={FiDatabase}
        title="Configure Cache"
        description="Cache helps speed up your workflow by storing frequently used data for quick access. Don't use cache if you're fetching data that changes frequently."
        helpIconContext="workflowStepCacheContext"
      />
      <Panel.Section title="Cache Settings" helpIconContext="workflowStepCacheContext">
        <ArgumentWrapper>
          <ArgumentLabel className={'small-gap'} label={'Condition'} description={'When should we cache this data?'} />
          <Select //
            disabled={disabled}
            placeholder={'Select a condition'}
            value={conditionOptions.find((n) => n.value === value?.condition)?.label}
            onChange={({ value }) => handleCacheChange('condition', value)}
            options={conditionOptions}
            useSearch={false}
          />
        </ArgumentWrapper>
        <ArgumentWrapper>
          <ArgumentLabel className={'small-gap'} label={'Expires In'} description={'How long do we wait before fetching new data?'} />
          <Select //
            disabled={disabled}
            placeholder={'Select a period'}
            value={ttlOptions.find((n) => n.value === value?.ttl)?.label}
            onChange={({ value }) => handleCacheChange('ttl', value)}
            options={ttlOptions}
            useSearch={false}
          />
        </ArgumentWrapper>
      </Panel.Section>
      <SliderClose onClose={onClose} />
    </>
  );
};

export default CacheSlider;
